import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Building2, Plus, Pencil, Trash2, FolderOpen, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formName, setFormName] = useState('');
  const [formProjects, setFormProjects] = useState([]);
  const [newProject, setNewProject] = useState('');
  const [saving, setSaving] = useState(false);

  const canManage = user?.role === 'admin' || user?.role === 'editor';
  const canDelete = user?.role === 'admin';

  const fetchClients = async () => {
    try { const res = await api.get('/clients'); setClients(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClients(); }, []);

  const openCreate = () => {
    setEditingClient(null); setFormName(''); setFormProjects([]); setDialog(true);
  };

  const openEdit = (client) => {
    setEditingClient(client); setFormName(client.name); setFormProjects([...client.projects]); setDialog(true);
  };

  const addProject = () => {
    const name = newProject.trim();
    if (!name || formProjects.includes(name)) return;
    setFormProjects(prev => [...prev, name]);
    setNewProject('');
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Client name required'); return; }
    setSaving(true);
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, { name: formName, projects: formProjects });
        toast.success('Client updated');
      } else {
        await api.post('/clients', { name: formName, projects: formProjects });
        toast.success('Client created');
      }
      setDialog(false); fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Delete this client?')) return;
    try { await api.delete(`/clients/${clientId}`); toast.success('Client deleted'); fetchClients(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground font-outfit tracking-tight">Clients</h1>
        {canManage && (
          <Button onClick={openCreate} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]" data-testid="add-client-btn">
            <Plus size={16} className="mr-2" /> Add Client
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-40 bg-[#FFF4E8] border border-border rounded-xl animate-pulse" />
          ))}
        </div>
      ) : clients.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Building2 className="mx-auto text-[#E7D2B8] mb-3" size={48} />
            <p className="text-muted-foreground text-lg">No clients yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map(client => (
            <Card
              key={client.id}
              className="bg-card border-border hover:border-[#C9B49A] hover:shadow-sm transition-all"
              data-testid={`client-card-${client.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base text-foreground font-outfit">{client.name}</CardTitle>
                  <div className="flex items-center gap-1">
                    {canManage && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600 hover:bg-[#FFF4E8]" onClick={() => openEdit(client)} data-testid={`edit-client-${client.id}`}>
                        <Pencil size={14} />
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500 hover:bg-red-50" onClick={() => handleDelete(client.id)} data-testid={`delete-client-${client.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-2">
                  <FolderOpen size={12} className="inline mr-1" />{client.projects.length} projects
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {client.projects.map(p => (
                    <Badge key={p} className="text-xs bg-[#F8EBD9] text-[#6E6258] border-[#E7D2B8]">{p}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editingClient ? 'Edit Client' : 'Add Client'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {editingClient ? 'Update client details and projects.' : 'Create a new client with projects.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#6E6258] font-medium mb-1.5 block">Client Name</label>
              <Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Client name" className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" data-testid="client-name-input" />
            </div>
            <div>
              <label className="text-sm text-[#6E6258] font-medium mb-1.5 block">Projects</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formProjects.map(p => (
                  <Badge key={p} className="bg-[#F8EBD9] text-[#6E6258] border-[#E7D2B8] gap-1 pr-1">
                    {p}
                    <button onClick={() => setFormProjects(prev => prev.filter(x => x !== p))} className="hover:text-red-500">
                      <X size={12} />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newProject}
                  onChange={e => setNewProject(e.target.value)}
                  placeholder="New project name"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] flex-1"
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addProject())}
                  data-testid="new-project-input"
                />
                <Button variant="outline" onClick={addProject} className="border-border text-foreground hover:bg-[#FFF4E8]" data-testid="add-project-btn">
                  <Plus size={16} />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]" data-testid="save-client-btn">
              {saving ? 'Saving...' : editingClient ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}